import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import AuthContext from '../../context/authContext';
import useGetResponseMarketing from '../../hooks/useGetResponseMarketing';

function formatDate(value) {
  if (!value) return '—';
  try {
    return dayjs(value).format('DD.MM.YYYY HH:mm');
  } catch (_) {
    return '—';
  }
}

const TABLE_SKELETON_ROW_COUNT = 8;

function GetResponseMarketingTableSkeletonRows() {
  return (
    <>
      {Array.from({ length: TABLE_SKELETON_ROW_COUNT }, (_, i) => (
        <TableRow key={`gr-skeleton-${i}`}>
          <TableCell>
            <Skeleton variant='text' width='72%' animation='wave' />
          </TableCell>
          <TableCell sx={{ minWidth: 280 }}>
            <Skeleton variant='rounded' height={40} animation='wave' />
          </TableCell>
          <TableCell>
            <Skeleton variant='text' width={140} animation='wave' />
          </TableCell>
          <TableCell>
            <Skeleton variant='text' width='88%' animation='wave' />
          </TableCell>
          <TableCell align='right'>
            <Skeleton variant='rounded' width={72} height={32} sx={{ ml: 'auto' }} animation='wave' />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

// Editable location tag with the derived default as placeholder; saves on
// blur/Enter (uncontrolled input to avoid per-keystroke re-renders).
function TagCell({ row, disabled, busy, onSave }) {
  const inputRef = useRef(null);

  const commit = () => {
    const value = (inputRef.current?.value || '').trim();
    if (value === String(row.getResponseTagName || '')) return;
    onSave(value);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 240 }}>
      <TextField
        size='small'
        fullWidth
        inputRef={inputRef}
        defaultValue={row.getResponseTagName || ''}
        placeholder={row.effectiveTagName || 'standort_…'}
        disabled={disabled || busy}
        onBlur={commit}
        onKeyDown={event => {
          if (event.key === 'Enter') commit();
        }}
      />
      {busy && <CircularProgress size={16} />}
    </Box>
  );
}

export default function GetResponseMarketingManagement() {
  const authCtx = useContext(AuthContext);
  const { getStatus, updateEntertainmentTag, postSync } = useGetResponseMarketing();

  const canRead = authCtx.checkRight('getresponse:read');
  const canUpdate = authCtx.checkRight('getresponse:update');
  const canSync = authCtx.checkRight('getresponse:sync');

  const [rows, setRows] = useState([]);
  const [mainList, setMainList] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [syncAllBusy, setSyncAllBusy] = useState(false);

  const load = useCallback(async () => {
    setLoadingData(true);
    try {
      const statusRes = await getStatus();
      setRows(statusRes?.entertainments || []);
      setMainList(statusRes?.mainList || null);
    } catch (e) {
      toast.error('Fehler beim Laden der GetResponse-Daten.');
      throw e;
    } finally {
      setLoadingData(false);
    }
  }, [getStatus]);

  useEffect(() => {
    if (!canRead) return;
    load().catch(() => {});
  }, [canRead, load]);

  const handleTagSave = async (entertainment, tagName) => {
    if (!canUpdate) return;
    setBusyId(entertainment._id);
    try {
      const updated = await updateEntertainmentTag(entertainment._id, tagName);
      setRows(prev =>
        prev.map(row => (row._id === entertainment._id ? { ...row, ...updated } : row))
      );
      toast.success(
        tagName ? 'Standort-Tag gespeichert.' : 'Standort-Tag auf Standard zurückgesetzt.'
      );
    } catch (e) {
      toast.error('Fehler beim Speichern des Standort-Tags.');
      throw e;
    } finally {
      setBusyId(null);
    }
  };

  const handleSyncOne = async id => {
    if (!canSync) return;
    setBusyId(id);
    try {
      await postSync(id);
      toast.success('Synchronisation gestartet und abgeschlossen.');
      await load();
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS') {
        toast.warn('Eine andere Synchronisation läuft bereits.');
      } else if (e?.code === 'GETRESPONSE_NOT_CONFIGURED') {
        toast.error('GetResponse ist auf dem Server nicht konfiguriert.');
      } else {
        toast.error('Fehler bei der Synchronisation.');
      }
      throw e;
    } finally {
      setBusyId(null);
    }
  };

  const handleSyncAll = async () => {
    if (!canSync) return;
    setSyncAllBusy(true);
    try {
      await postSync();
      toast.success('Globale Synchronisation abgeschlossen.');
      await load();
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS') {
        toast.warn('Eine andere Synchronisation läuft bereits.');
      } else if (e?.code === 'GETRESPONSE_NOT_CONFIGURED') {
        toast.error('GetResponse ist auf dem Server nicht konfiguriert.');
      } else {
        toast.error('Fehler bei der globalen Synchronisation.');
      }
      throw e;
    } finally {
      setSyncAllBusy(false);
    }
  };

  if (!canRead) {
    return <Navigate to='/welcome' replace />;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant='h5'>GetResponse Marketing</Typography>
        {canSync && (
          <Button
            variant='contained'
            startIcon={syncAllBusy ? <CircularProgress size={18} color='inherit' /> : <SyncIcon />}
            disabled={syncAllBusy || loadingData}
            onClick={() => handleSyncAll().catch(() => {})}
          >
            Alle synchronisieren
          </Button>
        )}
      </Box>

      {mainList?.resolvedId && (
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Hauptliste: {mainList.configuredName || mainList.configuredId || '—'} (
          {mainList.resolvedId})
        </Typography>
      )}
      {mainList?.error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          Die GetResponse-Hauptliste ist nicht auflösbar: {mainList.error}
        </Alert>
      )}

      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell>Entertainment</TableCell>
            <TableCell>Standort-Tag (leer = Standard)</TableCell>
            <TableCell>Zuletzt gesynct</TableCell>
            <TableCell>Ergebnis / Fehler</TableCell>
            <TableCell align='right'>Aktion</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loadingData ? (
            <GetResponseMarketingTableSkeletonRows />
          ) : (
            rows.map(row => {
              const sync = row.getResponseMarketingSync || {};
              return (
                <TableRow key={row._id}>
                  <TableCell>{row.longName || row.entertainmentName}</TableCell>
                  <TableCell sx={{ minWidth: 280 }}>
                    <TagCell
                      row={row}
                      disabled={!canUpdate}
                      busy={busyId === row._id}
                      onSave={tagName => {
                        handleTagSave(row, tagName).catch(() => {});
                      }}
                    />
                  </TableCell>
                  <TableCell>{formatDate(sync.lastSuccessAt || sync.lastAttemptAt)}</TableCell>
                  <TableCell>
                    <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                      {sync.lastResultSummary || sync.lastError || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align='right'>
                    {canSync && (
                      <Button
                        size='small'
                        variant='outlined'
                        startIcon={busyId === row._id ? <CircularProgress size={16} /> : <SyncIcon />}
                        disabled={busyId === row._id || syncAllBusy}
                        onClick={() => handleSyncOne(row._id).catch(() => {})}
                      >
                        Sync
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </Box>
  );
}
